"use client";

import React, { useState, useEffect } from "react";
import SmartImage from "~/components/reusables/SmartImage";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from "components/ui/dialog";
import { AspectRatio } from "~/components/ui/aspect-ratio";
import type { CarouselImage, ImageDimensions } from "~/lib/resolve-image-dimensions";

export type { CarouselImage };

/** CarouselImage with server-side resolved dimensions attached. */
export type CarouselImageWithDimensions = CarouselImage & {
  dimensions?: ImageDimensions;
};

type ImageCarouselProps = {
  images: CarouselImageWithDimensions[];
  dark?: boolean;
};

/** Derive a human-readable label from a URL when no caption is provided. */
function altFromUrl(url: string, index: number): string {
  try {
    const pathname = new URL(url).pathname;
    const filename = pathname.split("/").pop() ?? "";
    const name = filename.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ").trim();
    return name.length > 0 ? name : `Image ${index + 1}`;
  } catch {
    return `Image ${index + 1}`;
  }
}

export function ImageCarousel({ images, dark = false }: ImageCarouselProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [totalSlides, setTotalSlides] = useState<number>(0);

  useEffect(() => {
    if (!api) return;

    setTotalSlides(api.scrollSnapList().length);
    setCurrentIndex(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrentIndex(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <Carousel className="w-full px-10 py-3" opts={{ loop: true }} setApi={setApi}>
      <CarouselContent className="-ml-8">
        {images.map((image, index) => {
          const aspectRatio = image.dimensions?.aspectRatio ?? 16 / 9;
          const width = image.dimensions?.width ?? 1600;
          const height = image.dimensions?.height ?? 900;
          const isPortrait = aspectRatio < 1;

          return (
            <Dialog key={image.url}>
              <CarouselItem
                className={`pl-8 flex flex-col items-center ${isPortrait ? "xl:basis-1/2" : ""}`}
              >
                <div
                  className={`flex h-full flex-col items-center ${
                    isPortrait
                      ? "max-h-[580px] w-auto bg-white"
                      : "w-full"
                  } flex flex-col items-center justify-center overflow-hidden rounded-lg`}
                  style={isPortrait ? { minWidth: "420px" } : undefined}
                >
                  <AspectRatio ratio={aspectRatio}>
                    <DialogTrigger asChild>
                      <div className="relative h-full w-full cursor-pointer">
                        <SmartImage
                          src={image.url}
                          alt={image.caption || altFromUrl(image.url, index)}
                          fill
                          className="not-prose rounded-lg object-cover"
                          sizes={
                            isPortrait
                              ? "(max-width: 1280px) 100vw, 50vw"
                              : "100vw"
                          }
                        />
                      </div>
                    </DialogTrigger>
                  </AspectRatio>
                  {image.caption && (
                    <p className="mt-2 text-center text-sm text-gray-600">
                      {image.caption}
                    </p>
                  )}
                </div>

                {/* Lightbox */}
                <DialogContent
                  className={`w-full max-w-screen md:w-10/12 xl:max-w-7xl ${
                    dark ? "border-0 bg-black" : ""
                  }`}
                  overlayClassName={
                    dark ? "bg-black/85 backdrop-blur-sm" : "bg-white/80"
                  }
                  style={{
                    width: `${80 * aspectRatio}vh`,
                    maxWidth: "100vw",
                  }}
                >
                  <DialogTitle className="hidden">
                    {image.caption || altFromUrl(image.url, index)}
                  </DialogTitle>
                  <AspectRatio ratio={aspectRatio}>
                    <SmartImage
                      src={image.url}
                      alt={image.caption || altFromUrl(image.url, index)}
                      width={width}
                      height={height}
                      className="not-prose h-full w-full object-contain"
                      priority
                    />
                  </AspectRatio>
                  {image.caption && (
                    <p className="mt-2 text-center text-sm text-gray-600">
                      {image.caption}
                    </p>
                  )}
                </DialogContent>
              </CarouselItem>
            </Dialog>
          );
        })}
      </CarouselContent>

      {/* variant="secondary" gives a visible gray background so the arrow icons show.
          top-[calc(50%-26px)] shifts arrows up to center on the image area,
          compensating for the caption + dots below the image. */}
      <CarouselPrevious
        variant="secondary"
        className={`top-[calc(50%-26px)] ${
          dark
            ? "bg-white/20 text-white hover:bg-white/40"
            : "text-gray-700 shadow-sm hover:bg-slate-200"
        }`}
      />
      <CarouselNext
        variant="secondary"
        className={`top-[calc(50%-26px)] ${
          dark
            ? "bg-white/20 text-white hover:bg-white/40"
            : "text-gray-700 shadow-sm hover:bg-slate-200"
        }`}
      />

      {/* Pagination dots */}
      <div className="mt-4 flex justify-center gap-2">
        {Array.from({ length: totalSlides }).map((_, i) => (
          <button
            key={i}
            onClick={() => api?.scrollTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-2 w-2 rounded-full transition-all ${
              i === currentIndex
                ? dark ? "bg-white" : "bg-gray-800"
                : dark ? "bg-white/40" : "bg-gray-300"
            }`}
          />
        ))}
      </div>
    </Carousel>
  );
}
